import { useState, useCallback } from 'react';
import { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { apiService } from 'services/base.service';

interface UseAxiosProps {
  url: string;
  method?: 'get' | 'post' | 'put' | 'delete' | 'patch';
  body?: any;
  headers?: any;
  baseURL?: string;
  isInternal?: boolean;
}

interface UseAxiosReturn<T> {
  response: T | null;
  error: AxiosError | null;
  loading: boolean;
  fetchData: (config?: AxiosRequestConfig, body?: any) => Promise<void>;
}

const useAxios = <T>({
  url,
  method = 'get',
  body = null,
  baseURL,
  isInternal,
  headers = {}
}: UseAxiosProps): UseAxiosReturn<T> => {
  const [response, setResponse] = useState<T | null>(null);
  const [error, setError] = useState<AxiosError | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (config: AxiosRequestConfig = {}, fetchDatabody?: any) => {
      setLoading(true);
      setError(null);
      try {
        const res: AxiosResponse<T> = await apiService[method](url, {
          method,
          baseURL,
          isInternal,
          data: fetchDatabody ?? body,
          headers,
          ...config
        });

        setResponse(res?.data as T);
      } catch (err) {
        setError(err as AxiosError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [url, method, body, headers]
  );

  return { response: response, error, loading, fetchData };
};

export default useAxios;
